import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/layoutChrome";
import { ModuleFeatureCard } from "@/components/HubModuleCard";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreviewActions } from "@/lib/usePlatformDesignerBuyerPreview";
import { resolveModuleNavIcon } from "@/modules/moduleNavIcons";
import { getDesignerHubPrimaryCards } from "@/pages/disenador/designerHubSections";
import { Building2, LayoutDashboard } from "lucide-react";
import DesignerBuyerUxPreferences from "@/components/disenador/DesignerBuyerUxPreferences";

/**
 * Hub de inicio del Módulo Maestro Diseñador — tarjetas principales agrupadas.
 * platform_designer conserva acceso global; subherramientas viven en rutas secundarias.
 */
export default function DesignerMasterHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const { enableBuyerPreview } = usePlatformDesignerBuyerPreviewActions();
  const canUseDesignerHub = Boolean(isPlatformDesigner || user?.can_generate_licenses);

  const primaryCards = useMemo(
    () => getDesignerHubPrimaryCards({ isPlatformDesigner }),
    [isPlatformDesigner],
  );

  if (user && !canUseDesignerHub) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <p className="text-sm text-slate-600">No tiene acceso al Módulo Maestro Diseñador.</p>
      </div>
    );
  }

  const openCard = (card) => {
    if (card.action === "enable-buyer-preview") {
      enableBuyerPreview();
      navigate(card.path || "/");
      return;
    }
    navigate(card.path);
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Inicio del módulo Diseñador"
        subtitle="Centro administrativo y comercial BNT — accesos principales agrupados."
        breadcrumb={["Módulo Maestro Diseñador", "Inicio"]}
      />

      <div className="p-8 max-w-6xl mx-auto space-y-8" data-testid="designer-master-hub">
        <div className="rounded-md border border-[#0F2D5C]/15 bg-[#0F2D5C]/5 px-4 py-3 text-sm text-slate-700 flex gap-3">
          <Building2 className="w-5 h-5 shrink-0 text-[#0F2D5C]" aria-hidden />
          <p className="m-0 leading-relaxed">
            Entradas principales agrupadas por función. Licencias distribuidor (gestor/cartera), Centro de
            Licencias SaaS BNT y herramientas técnicas SAT viven en rutas dedicadas. Los usuarios internos
            de cada despacho se administran en <strong>Gestión Despacho</strong>, no aquí.
          </p>
        </div>

        {isPlatformDesigner ? <DesignerBuyerUxPreferences /> : null}

        <section aria-label="Accesos principales del módulo Diseñador" data-testid="designer-hub-primary-cards">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {primaryCards.map((card) => {
              const Icon = resolveModuleNavIcon(card.iconKey, LayoutDashboard);
              return (
                <ModuleFeatureCard
                  key={card.id}
                  label={card.label}
                  description={card.description}
                  icon={Icon}
                  hasModuleAccess
                  accessLoading={false}
                  testId={card.testid}
                  cardCategory={card.category}
                  cardStatus={card.status}
                  onEnter={() => openCard(card)}
                />
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
